import { BreakpointTrackerContext } from '@jutro/layout';
import { useHistory } from 'react-router-dom';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Loader, useModal } from '@jutro/components';
import { CheckboxField } from '@jutro/legacy/components';
import { FieldLinkComponent } from 'gw-components-platform-react';
import { useTranslator } from '@jutro/locale';
import { UserService } from 'gw-capability-gateway';
import TWIABatchService from "ent-capability-policyportfolio/services/TWIABatchService";
import { format } from 'date-fns';
import metadata from './TWIABatchesLanding.metadata.json5';
import messages from './TWIABatchesLanding.messages';
import styles from './TWIABatchesLanding.module.scss';
import ReportComponent from '../ReportComponent/ReportComponent';

function TWIABatchesLanding() {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { authHeader } = useAuthentication();
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const [isLoading, setIsLoading] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const [showBatches, setShowBatches] = useState(false);
    const [showReports, setShowReports] = useState(false);
    const [batchVM, updateBatchVM] = useState({});
    const [transferTableData, setTransferTableData] = useState([]);
    const history = useHistory();
    const [selectedBatchCodes, setSelectedBatchCodes] = useState([]);
    const { refreshData } = useDataRefresh();
    const [currentView, setCurrentView] = useState('');

    useEffect(() => {
        setIsLoading(true);
        checkEditBulkProducerChangePermission();
        loadBatches();
        setShowBatches(true);
        setIsLoading(false);
        setCurrentView('transferTile');

        const update = viewModelService.create(
            {},
            'pc',
            'edge.capabilities.gateway.bulkproducerchange.dto.TWIABPCBatch_ExtSummaryDTO'
        );

        updateBatchVM(update);
    }, [viewModelService]);

    const checkEditBulkProducerChangePermission = useCallback(async () => {
        const permissionDTO = {
            permission: 'editbulkproducerchange_ext'
        };

        const permissionCheckPromise = await UserService.hasUserSystemPermission(permissionDTO, authHeader);

        _.set(batchVM, 'hasEditBulkProducerChangePermission', permissionCheckPromise);
        updateBatchVM(batchVM);

        return permissionCheckPromise;
    }, [authHeader, batchVM]);


    const getStatus = useCallback((statusCode) => {
        const typelist = viewModelService?.productMetadata?.get('pc').types.getTypelist('BPCBatchStatus_Ext');
        const formattedTypes = typelist?.codes.map((code) => ({
            code: code.code,
            name: translator({ id: code.name })
        }))
        const matchingType = formattedTypes?.find((type) => type.code === statusCode);

        return matchingType ? matchingType.name : null;
    }, [translator, viewModelService?.productMetadata]);

    const getBatchType = useCallback((batchTypeCode) => {
        const typelist = viewModelService?.productMetadata?.get('pc').types.getTypelist('BPCType_Ext');
        const formattedTypes = typelist?.codes.map((code) => ({
            code: code.code,
            name: translator({ id: code.name })
        }))
        const matchingType = formattedTypes?.find((type) => type.code === batchTypeCode);

        return matchingType ? matchingType.name : null;
    }, [translator, viewModelService?.productMetadata]);

    const getSource = useCallback((sourceCode) => {
        const typelist = viewModelService?.productMetadata?.get('pc').types.getTypelist('BPCSource_Ext');
        const formattedTypes = typelist?.codes.map((code) => ({
            code: code.code,
            name: translator({ id: code.name })
        }))
        const matchingType = formattedTypes?.find((type) => type.code === sourceCode);

        return matchingType ? matchingType.name : null;
    }, [translator, viewModelService?.productMetadata]);

    const processBatches = useCallback((batches) => {
        const batchMap = batches.map(batch => ({
            checked: false,
            batchCode: batch.batchCode,
            source: getSource(batch.source),
            batchType: getBatchType(batch.batchType),
            newProducerCode: batch.newProducerCode,
            targetDate: format(new Date(batch.targetDate), 'dd/MM/yyyy'),
            status: getStatus(batch.status)
        }));

        _.set(batchVM, 'Batches_Ext', batchMap);
        updateBatchVM(batchVM);
        setTransferTableData(batchMap);

    }, [batchVM, getBatchType, getSource, getStatus]);

    const loadBatches = useCallback(async () => {
        const serviceResponse = await TWIABatchService.findAllViewableBatchesForCurrentUser(authHeader);

        processBatches(serviceResponse);

        return serviceResponse;
    }, [authHeader, processBatches]);

    const handleTilesOnClick = useCallback((id) => {
        setCurrentView(id);

        if (id === "transferTile") {
            toggleBatches();
        } else {
            toggleReports();
        }
    }, []);

    const isCheckboxVisibleForBatch = useCallback((batch) =>
        (batch.status === 'Pending Approval' || batch.status === 'Approved') && _.get(batchVM, 'hasEditBulkProducerChangePermission'),
        []);


    const getCheckedBatchCodes = (e, item) => {
        const isChecked = !item.checked;

        item.checked = isChecked;

        if (isChecked) {
            setSelectedBatchCodes(prev => [...prev, item.batchCode]);
        } else {
            setSelectedBatchCodes(prev => prev.filter(code => code !== item.batchCode));
        }
    };

    const getCheckBox = useCallback(
        (item, index) => {
            const isVisible = isCheckboxVisibleForBatch(item);

            return isVisible ? (
                <CheckboxField
                    name={item.displayName}
                    id={item.displayName}
                    value={item.checked}
                    onValueChange={e => getCheckedBatchCodes(e, item, index)}
                />
            ) : null;
        },
        [isCheckboxVisibleForBatch]
    );



    const onDelete = useCallback(async () => {
        const batchCodes = selectedBatchCodes

        if (batchCodes.length === 0) {
            showAlert({
                title: translator(messages.bbtRequest),
                message: translator(messages.selectDelMsg),
                status: 'error',
                icon: 'mi-error-outline',
                confirmButtonText: translator(messages.close)
            });

        } else {
            const deleteDialogPromise = {
                title: translator(messages.bbtRequest),
                message: translator(messages.deleteNewBatchMsg),
                status: 'warning',
                icon: 'gw-help-outline',
                confirmButtonText: translator(messages.yes),
                cancelButtonText: translator(messages.no)
            };

            const result = await showConfirm(deleteDialogPromise);

            if (result === 'confirm') {
                const promise = TWIABatchService.deleteBatches({ batchCodes }, authHeader);

                promise.then(
                    () => {
                        showAlert({
                            title: translator(messages.bbtRequest),
                            message: translator(messages.transferReqDelMsg),
                            status: 'info',
                            icon: 'mi-info-outline'
                        });
                        setSelectedBatchCodes([]);
                        setTransferTableData(prevData => prevData.filter(item => !batchCodes.includes(item.batchCode)));
                        refreshData();
                    },
                    () => {
                        showAlert({
                            title: translator(messages.deleteFailed),
                            message: translator(messages.sorryMsg),
                            status: 'error',
                            icon: 'mi-error-outline',
                            confirmButtonText: translator(messages.close)
                        });
                    }
                );
            }
        }
    }, [selectedBatchCodes, showAlert, translator, showConfirm, authHeader, refreshData]);

    const onAddClick = useCallback(() => {
        history.push(`/new-batch`);
    }, [history]);

    const toggleBatches = () => {
        setCurrentView('transferTile')
        setShowBatches(true);
        setShowReports(false);
    };

    const toggleReports = () => {
        setCurrentView('reportTile')
        setShowBatches(false);
        setShowReports(true);
    };

    const redirectTransferDetails = (item, index, { id: property }) =>
    (
        <FieldLinkComponent
            id={item.batchCode}
            value={item[property]}
            to={`/detail/${item[property]}`}
        />
    );

    const overrideProps = {
        '@field': {
            // apply to all fields
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        [currentView]: {
            active: true,
        },
        trasnferTitlelayout: {
            visible: currentView === 'transferTile'
        },
        reportTitlelayout: {
            visible: currentView === 'reportTile'
        },
        transferTableGrid: {
            data: transferTableData
        },
        buttonsContainer: {
            visible: showBatches
        },
        transferTableContainer: {
            visible: showBatches
        },
        reportComponent: {
            visible: showReports
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            reportComponent: ReportComponent,
        },
        resolveCallbackMap: {
            handleTilesOnClick,
            onDelete,
            onAddClick,
            getCheckBox,
            redirectTransferDetails
        },

    }

    return (
        <Loader loaded={!isLoading}>
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={batchVM}
                overrideProps={overrideProps}
                onModelChange={updateBatchVM}
                callbackMap={resolvers.resolveCallbackMap}
                classNameMap={resolvers.resolveClassNameMap}
                componentMap={resolvers.resolveComponentMap}
            />
        </Loader>
    );
}

export default TWIABatchesLanding;